import { AdminFirestoreAdapter, allFromSnapshot, ClientFirestoreAdapter, } from "@buildwithflux/firestore-compatibility-layer";
/*
 * Get a part from firestore by its uid.
 * QUESTION: what if the part does not exist? it seems like this function should
 * return undefined
 */
const getPartByUid = (partUid, db) => {
    return db
        .part(partUid)
        .get()
        .then((result) => {
        return result.data();
    })
        .catch((error) => {
        // Check for specific Firestore errors that should return undefined. "invalid-argument"
        // catches scenarios where an invalid uuid is provided (eg when our copilot provides
        // an MPN where a uuid is needed). We want to treat these the same as a general not-found,
        // because from our perspective they basically are that, since there's no always-correct
        // logic to tell if a uuid is valid in advance. (While we could eliminate some bad-format
        // uuids, it would always be a subset of all invalid uuids, so why distinguish?)
        if (error.code === "not-found" || error.code === "invalid-argument") {
            return undefined;
        }
        // Don't suppress general errors, though. They may be important.
        throw error;
    });
};
const getAllParts = async (db) => {
    return allFromSnapshot(await db.partCollection().get());
};
/*
 * Save a part to firestore.
 */
const savePart = (part, db) => {
    return db.part(part.uid).set(part).then();
};
/*
 * Update only specific fields in firestore.
 */
const updatePart = (partUid, fields, db) => {
    return db.part(partUid).update(fields).then();
};
/*
 * Destroy a part in firestore.
 */
const destroyPart = (partUid, db) => {
    return db.part(partUid).delete().then();
};
/*
 * Subscribe to changes on a part.
 */
const subscribeToPartChanges = (partUid, onChange, db) => {
    return db.part(partUid).onSnapshot((partSnapshot) => {
        if (partSnapshot.exists) {
            const result = partSnapshot.data();
            //TODO: if result not defined, that'd be a case for throwing an exception?
            if (result)
                onChange(result);
        }
    });
};
/**
 * Creates a new part and a new part version under that part in firestore.
 *
 * Note that we should use transaction to perform such write operations in order to avoid
 * any potential race condition.
 *
 * For example, if the write to `part` collection and `partVersion` sub-collection ain't grouped
 * in the same transaction, it might result in race condition in `syncAlgoliaPartIndex` trigger,
 * which tries to "assemble" a search document data from this new part and new partVersion data, and hence
 * fail to sync that data to Algolia
 */
const publishNewPart = (db, newPart, newPartVersion) => {
    // This is the first time we create this part and part version so this transaction
    // should be safe in a sense that these data are not modified outside.
    // DS: These two transactions don't seem to be able to be combined at a type level - good luck to any future optimizer!
    if (db instanceof AdminFirestoreAdapter) {
        return db.runTransaction(async (t) => {
            const partRef = db.part(newPart.uid);
            const partVersionRef = db.partVersion(newPart.uid, newPartVersion.version);
            t.set(partRef, newPart);
            t.set(partVersionRef, newPartVersion);
        });
    }
    if (db instanceof ClientFirestoreAdapter) {
        return db.runTransaction(async (t) => {
            const partRef = db.part(newPart.uid);
            const partVersionRef = db.partVersion(newPart.uid, newPartVersion.version);
            t.set(partRef, newPart);
            t.set(partVersionRef, newPartVersion);
        });
    }
    throw new Error("Unknown type of adapter");
};
/**
 * Bind the above functions to a specific firestore instance to create a PartStorageApi object.
 *
 * @todo Use a class
 */
export const createFirestorePartRepository = (db) => {
    return {
        get: (uid) => {
            return getPartByUid(uid, db);
        },
        getAll: () => {
            return getAllParts(db);
        },
        update: (partUid, fields) => {
            return updatePart(partUid, fields, db);
        },
        save: (item) => {
            return savePart(item, db);
        },
        publishNewPart: (part, partVersion) => {
            return publishNewPart(db, part, partVersion);
        },
        destroy: (partUid) => {
            return destroyPart(partUid, db);
        },
        subscribe: (uid, onChange) => {
            return subscribeToPartChanges(uid, onChange, db);
        },
    };
};
//# sourceMappingURL=FirestorePartRepository.js.map